"use client"

import { useState, useEffect, useRef } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { TabGroup, TabGroupList, TabGroupTrigger, TabGroupContent } from "@/components/ui/tab-group"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Upload, Loader2 } from "lucide-react"
import { SlipResultDialog, mapResultToVariant } from "./slip-result-dialog"
import { Branch, BankAccount } from "@/src/types/slip"

const THAI_BANKS = [
  { value: "BBL",   label: "ธนาคารกรุงเทพ",                               icon: "bbl",   color: "#1e4598" },
  { value: "KTB",   label: "ธนาคารกรุงไทย",                               icon: "ktb",   color: "#1ba5e1" },
  { value: "BAY",   label: "ธนาคารกรุงศรีอยุธยา",                         icon: "bay",   color: "#fec43b" },
  { value: "KBANK", label: "ธนาคารกสิกรไทย",                              icon: "kbank", color: "#138f2d" },
  { value: "KK",    label: "ธนาคารเกียรตินาคิน",                          icon: "kk",    color: "#199cc5" },
  { value: "CITI",  label: "ซิตี้แบงก์ประเทศไทย",                        icon: "citi",  color: "#1583c7" },
  { value: "CIMB",  label: "ธนาคารซีไอเอ็มบีไทย",                        icon: "cimb",  color: "#7e2f36" },
  { value: "TMB",   label: "ธนาคารทหารไทย",                               icon: "tmb",   color: "#1279be" },
  { value: "SCB",   label: "ธนาคารไทยพาณิชย์",                           icon: "scb",   color: "#4e2e7f" },
  { value: "TCRB",  label: "ธนาคารไทยเครดิตเพื่อรายย่อย",                icon: "tcrb",  color: "#0a4ab3" },
  { value: "TBANK", label: "ธนาคารธนชาติ",                                icon: "tbank", color: "#fc4f1f" },
  { value: "TISCO", label: "ธนาคารทิสโก้",                                icon: "tisco", color: "#12549f" },
  { value: "IBANK", label: "ธนาคารอิสลามแห่งประเทศไทย",                  icon: "ibank", color: "#184615" },
  { value: "EXIM",  label: "ธนาคารเพื่อการส่งออกและนำเข้าแห่งประเทศไทย", icon: null,    color: "#636363" },
  { value: "BAAC",  label: "ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (ธ.ก.ส.)", icon: "baac", color: "#4b9b1d" },
  { value: "UOB",   label: "ธนาคารยูโอบี",                                icon: "uob",   color: "#0b3979" },
  { value: "GSB",   label: "ธนาคารออมสิน",                                icon: "gsb",   color: "#eb198d" },
  { value: "GHB",   label: "ธนาคารอาคารสงเคราะห์",                       icon: "ghb",   color: "#f57d23" },
  { value: "TTB",   label: "ธนาคารทหารไทยธนชาต",                         icon: "ttb",   color: "#0a3878" },
]
import { verifySlip, createManualSlip } from "@/src/lib/api/slip-api"
import { verifySlipSchema, manualSlipSchema, parseAmountInput } from "@/src/lib/validations/slip-schemas"
import { z } from "zod"

interface SubmitResult {
  mode: 'verify' | 'manual'
  success: boolean
  errorMessage?: string
  // Shared
  transaction_ref?: string
  sender_name?: string
  amount?: number
  transaction_date?: string
  account_label?: string
  // Verify-only
  is_duplicate?: boolean
  bank_code?: string
  verification_method?: string
  credits_used?: number
  credits_saved?: number
  credits_remaining?: number
  classification_type?: string
  warnings?: Array<{ type: string; severity: string; message: string }>
  // Insufficient credits
  credit_required?: number
  credit_available?: number
  credit_shortfall?: number
  receiver_name?: string
  receiver_account?: string
  sender_account?: string
  branch_name?: string
  matched_transaction_data?: Record<string, unknown> | null
}

interface AddSlipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  branches: Branch[]
  accounts: BankAccount[]
  onSuccess?: () => void
}

export function AddSlipDialog({ open, onOpenChange, branches, accounts, onSuccess }: AddSlipDialogProps) {
  const bankDropdownRef = useRef<HTMLDivElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState("check")
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)
  
  // Verify Slip Form State
  const [verifyBranchId, setVerifyBranchId] = useState("")
  
  // Manual Slip Form State
  const [manualAccountId, setManualAccountId] = useState("")
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(e.target as Node)) {
        setBankDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])
  const [manualBankCode, setManualBankCode] = useState("")
  const [manualSenderName, setManualSenderName] = useState("")
  const [manualAmount, setManualAmount] = useState("")
  const [manualTransferDate, setManualTransferDate] = useState("")
  const [manualStatus, setManualStatus] = useState<"success" | "pending" | "failed">("success")

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
      setError(null)
    }
  }

  const handleVerifySlip = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      const data = verifySlipSchema.parse({
        branch_id: verifyBranchId,
        image: selectedFile,
      })

      const response = await verifySlip({
        branch_id: data.branch_id,
        image: data.image,
        channel: 'WEB_DASHBOARD',
      })

      // --- Extract useful fields from the rich backend response ---
      const tx = response.transaction
      const isDup = response.deduplication?.is_duplicate ?? (tx as any)?.is_duplicate ?? false
      const senderObj = (tx as any)?.sender
      // merchant_name is the receiver/payee — do NOT use as sender fallback
      const senderName = senderObj?.displayName ?? senderObj?.name ?? undefined
      const senderAcc  = typeof senderObj?.account === 'object' ? (senderObj?.account as any)?.value : undefined
      const receiverObj = (tx as any)?.receiver
      const receiverName = receiverObj?.displayName ?? receiverObj?.name ?? undefined
      const receiverAcc  = typeof receiverObj?.account === 'object'
        ? (receiverObj?.account as any)?.value
        : response.verification?.receiverAccountNumber ?? undefined
      const cost = typeof response.cost === 'object' ? response.cost : null

      onSuccess?.() // refresh the table / dashboard stats in background

      setSubmitResult({
        mode: 'verify',
        success: response.success,
        transaction_ref: (tx as any)?.ref ?? tx?.transaction_ref ?? response.operation_id,
        sender_name: senderName,
        sender_account: senderAcc,
        receiver_name: receiverName ?? response.business_context?.account_name,
        receiver_account: receiverAcc,
        // For duplicates, QR parsing may not include amount — use matched transaction's stored amount
        amount: (isDup && response.deduplication?.matched_transaction_data?.amount != null)
          ? (response.deduplication.matched_transaction_data as any).amount
          : (tx as any)?.amount,
        transaction_date: (tx as any)?.transaction_date,
        bank_code: (tx as any)?.bank_code ?? response.verification?.bank_code,
        is_duplicate: isDup,
        verification_method: response.verification?.method,
        credits_used: cost?.credits_deducted ?? (cost as any)?.credits_used ?? 0,
        credits_saved: cost?.credits_saved ?? 0,
        credits_remaining: cost?.remaining_balance ?? (cost as any)?.credits_remaining,
        // For duplicates, inherit the original record's transaction_type so the dialog
        // shows the correct "รายจ่าย" / "รายรับ" badge instead of the INCOME default fallback.
        classification_type: isDup
          ? ((response.deduplication?.matched_transaction_data as any)?.transaction_type?.toUpperCase()
              ?? response.classification?.type)
          : response.classification?.type,
        warnings: response.warnings ?? [],
        branch_name: response.business_context?.branch_name,
        account_label: response.business_context?.account_name,
        matched_transaction_data: response.deduplication?.matched_transaction_data ?? null,
      })
    } catch (err) {
      const axiosErr = err as any
      if (axiosErr?.response?.status === 402 || axiosErr?.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        const details = axiosErr?.response?.data?.details || {}
        setSubmitResult({
          mode: 'verify',
          success: false,
          errorMessage: 'INSUFFICIENT_CREDITS',
          credit_required: details.required,
          credit_available: details.available,
          credit_shortfall: details.shortfall,
        })
      } else {
        let msg = 'เกิดข้อผิดพลาดในการตรวจสอบสลิป'
        if (err instanceof z.ZodError) { msg = err.errors[0].message }
        else if (err instanceof Error) { msg = err.message }
        setSubmitResult({ mode: 'verify', success: false, errorMessage: msg })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleManualSlip = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(null)
      
      // Validate form
      const data = manualSlipSchema.parse({
        account_id: manualAccountId,
        bank_code: manualBankCode,
        sender_name: manualSenderName,
        amount: parseAmountInput(manualAmount),
        transfer_date: manualTransferDate,
        status: manualStatus,
        image: selectedFile || undefined,
      })
      
      // Call API
      const response = await createManualSlip(data)

      // Find account label for display
      const account = accounts.find(a => a.id === data.account_id)
      const accountLabel = account
        ? `${account.bank_code} - ${account.account_number}`
        : data.account_id

      if (response.success) {
        setSubmitResult({
          mode: 'manual',
          success: true,
          transaction_ref: response.transaction?.transaction_ref ?? undefined,
          sender_name: data.sender_name,
          amount: data.amount,
          transaction_date: data.transfer_date,
          account_label: accountLabel,
        })
        onSuccess?.()  // refresh parent table / dashboard stats in background
      } else {
        setSubmitResult({
          mode: 'manual',
          success: false,
          errorMessage: 'การบันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
        })
      }
    } catch (err) {
      let msg = 'เกิดข้อผิดพลาดในการบันทึกสลิป'
      if (err instanceof z.ZodError) { msg = err.errors[0].message }
      else if (err instanceof Error) { msg = err.message }
      setSubmitResult({ mode: 'manual', success: false, errorMessage: msg })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setError(null)
    setSuccess(null)
    setSubmitResult(null)
    setVerifyBranchId("")
    setManualAccountId("")
    setManualBankCode("")
    setManualSenderName("")
    setManualAmount("")
    setManualTransferDate("")
    setManualStatus("success")
    onOpenChange(false)
  }

  const getCurrentDateTime = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = (now.getMonth() + 1).toString().padStart(2, '0')
    const day = now.getDate().toString().padStart(2, '0')
    const hours = now.getHours().toString().padStart(2, '0')
    const minutes = now.getMinutes().toString().padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        <DialogHeader className="sr-only">
          <DialogTitle>ส่งสลิปตรวจสอบ</DialogTitle>
          <DialogDescription>
            เลือกวิธีการส่งสลิปเพื่อตรวจสอบ: ตรวจสลิปอัตโนมัติหรือบันทึกแบบแมนนวล
          </DialogDescription>
        </DialogHeader>
        
        {/* Header — hidden when result screen is active (SlipResultDialog renders its own) */}
        {!submitResult && (
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-gray-900" aria-hidden="true">
              ส่งสลิปตรวจสอบ
            </h2>
          </div>
        )}

        {/* Result screen — replaces the form after submission */}
        {submitResult && (
          <SlipResultDialog
            variant={mapResultToVariant(submitResult)}
            data={submitResult}
            onClose={handleClose}
            onRetry={() => setSubmitResult(null)}
          />
        )}

        {/* Content */}
        {!submitResult && (<>
        <div className="px-6 pb-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          <TabGroup defaultValue="check" color="brand-blue" className="w-full" onValueChange={setActiveTab}>
            <TabGroupList className="grid grid-cols-2 w-full">
              <TabGroupTrigger value="check">ตรวจสลิป</TabGroupTrigger>
              <TabGroupTrigger value="manual">แมนนวลสลิป</TabGroupTrigger>
            </TabGroupList>

            <TabGroupContent value="check" className="space-y-4 mt-4">
              {/* Branch Select */}
              <div className="space-y-2">
                <Label htmlFor="branch" className="text-sm font-medium text-gray-900">
                  เลือกสาขา<span className="text-red-500">*</span>
                </Label>
                <Select value={verifyBranchId} onValueChange={setVerifyBranchId}>
                  <SelectTrigger id="branch">
                    <SelectValue placeholder="เลือกสาขา" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'white' }}>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.branch_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Picker */}
              <div className="space-y-2">
                <Label htmlFor="date" className="text-sm font-medium text-gray-900">
                  วันที่ส่งตรวจ<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="text"
                  value={getCurrentDateTime()}
                  className="bg-gray-50"
                  readOnly
                />
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-900">
                  รูปสลิปโอนเงิน<span className="text-red-500">*</span>
                </Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    id="slip-upload"
                    className="hidden"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="slip-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-gray-400" />
                      <span className="text-lg font-medium text-gray-600">
                        {selectedFile ? selectedFile.name : "อัพโหลดสลิป"}
                      </span>
                      <span className="text-sm text-gray-400">
                        เฉพาะไฟล์ .jpg / .jpeg / .png (ขนาดไม่เกิน 20MB)
                      </span>
                    </div>
                  </label>
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-3 bg-gray-300 hover:bg-gray-400"
                    onClick={() => document.getElementById("slip-upload")?.click()}
                  >
                    เลือกไฟล์
                  </Button>
                </div>
                <div className="space-y-1 text-xs text-red-500">
                  <p>*ระบบจะตรวจสอบข้อมูลสลิปกับธนาคารและบันทึก ข้อมูลในร้านค้าที่ท่านเลือก</p>
                  <p>*ระบบจะหักโควต้าตามแพ็คเกจปัจจุบันของร้านค้านั้น</p>
                </div>
              </div>
            </TabGroupContent>

            <TabGroupContent value="manual" className="space-y-4 mt-4">
              {/* Receiver Select */}
              <div className="space-y-2">
                <Label htmlFor="receiver" className="text-sm font-medium text-gray-900">
                  เลือกบัญชี<span className="text-red-500">*</span>
                </Label>
                <Select value={manualAccountId} onValueChange={setManualAccountId}>
                  <SelectTrigger id="receiver">
                    <SelectValue placeholder="เลือกบัญชี" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'white' }}>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.bank_code} - {account.account_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                {/* Slip Image Upload */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-900">
                    รูปสลิปโอนเงิน (ถ้ามี)
                  </Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      id="manual-slip-upload"
                      className="hidden"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="manual-slip-upload" className="cursor-pointer">
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-gray-400" />
                        <span className="text-lg font-medium text-gray-600">
                          {selectedFile ? selectedFile.name : "อัพโหลดสลิป"}
                        </span>
                        <span className="text-sm text-gray-400">
                          เฉพาะไฟล์ .jpg / .jpeg / .png (ขนาดไม่เกิน 20MB)
                        </span>
                      </div>
                    </label>
                    <Button
                      type="button"
                      variant="secondary"
                      className="mt-3 bg-gray-300 hover:bg-gray-400"
                      onClick={() => document.getElementById("manual-slip-upload")?.click()}
                    >
                      เลือกไฟล์
                    </Button>
                  </div>
                </div>

                <div>
                  {/* Bank Selection */}
                  <div className="space-y-2 mb-3">
                    <Label className="text-sm font-medium text-gray-900">
                      ธนาคารผู้โอน<span className="text-red-500">*</span>
                    </Label>
                    <div className="relative" ref={bankDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setBankDropdownOpen(!bankDropdownOpen)}
                        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        {manualBankCode ? (
                          <span className="flex items-center gap-2">
                            {(() => {
                              const bank = THAI_BANKS.find(b => b.value === manualBankCode)
                              return bank?.icon ? (
                                <span className="w-5 h-5 rounded-sm flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: bank.color }}>
                                  <img src={`/icons/banks-logo/th/${bank.icon}.svg`} alt="" className="w-4 h-4" />
                                </span>
                              ) : null
                            })()}
                            <span>{THAI_BANKS.find(b => b.value === manualBankCode)?.label}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">เลือกธนาคารผู้โอน</span>
                        )}
                        <svg className="h-4 w-4 opacity-50 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
                      </button>
                      {bankDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
                          {THAI_BANKS.map((bank) => (
                            <button
                              key={bank.value}
                              type="button"
                              onClick={() => { setManualBankCode(bank.value); setBankDropdownOpen(false) }}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 ${
                                manualBankCode === bank.value ? 'bg-gray-50 font-medium' : ''
                              }`}
                            >
                              {bank.icon ? (
                                <span className="w-6 h-6 rounded-sm flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: bank.color }}>
                                  <img src={`/icons/banks-logo/th/${bank.icon}.svg`} alt="" className="w-5 h-5" />
                                </span>
                              ) : (
                                <span className="w-6 h-6 rounded-sm flex-shrink-0 inline-flex items-center justify-center" style={{ backgroundColor: bank.color }} />
                              )}
                              <span>{bank.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sender Name */}
                  <div className="space-y-2">
                    <Label htmlFor="sender-name" className="text-sm font-medium text-gray-900">
                      ชื่อผู้โอน<span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="sender-name"
                      type="text"
                      placeholder="กรอกชื่อผู้โอน"
                      value={manualSenderName}
                      onChange={(e) => setManualSenderName(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Transfer Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="transfer-date" className="text-sm font-medium text-gray-900">
                    วันเวลาตามสลิป<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="transfer-date"
                    type="datetime-local"
                    value={manualTransferDate}
                    onChange={(e) => setManualTransferDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="check-date" className="text-sm font-medium text-gray-900">
                    วันที่ส่งตรวจ<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="check-date"
                    type="text"
                    value={getCurrentDateTime()}
                    className="bg-gray-50"
                    readOnly
                  />
                </div>
              </div>

              {/* Amount and Status */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-sm font-medium text-gray-900">
                    จำนวนเงิน<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="amount"
                    type="text"
                    placeholder="กรอกจำนวนเงิน"
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm font-medium text-gray-900">
                    สถานะ<span className="text-red-500">*</span>
                  </Label>
                  <Select value={manualStatus} onValueChange={(value: "success" | "pending" | "failed") => setManualStatus(value)}>
                    <SelectTrigger id="status">
                      <SelectValue placeholder="เลือกสถานะ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="success">สำเร็จ</SelectItem>
                      <SelectItem value="pending">รอดำเนินการ</SelectItem>
                      <SelectItem value="failed">ไม่สำเร็จ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <hr className="border-gray-300" />

              {/* Warning Text */}
              <div className="space-y-1 text-xs text-red-500">
                <p>*ระบบจะบันทึกข้อมูลสลิป โดยไม่ทำการตรวจสอบกับธนาคารและไม่มีการหักโควต้าใดๆเพิ่มเติม</p>
              </div>
            </TabGroupContent>
          </TabGroup>
        </div>

        {/* Footer Actions */}
        <div className="grid grid-cols-3 items-center justify-between gap-3 px-6 py-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            ปิด
          </Button>
          <div></div>
          <Button
            onClick={() => {
              if (activeTab === 'check') {
                handleVerifySlip()
              } else {
                handleManualSlip()
              }
            }}
            className="bg-green-500 hover:bg-green-600 text-white"
            disabled={loading || (activeTab === 'check' && !verifyBranchId)}
            title={activeTab === 'check' && !verifyBranchId ? 'กรุณาเลือกสาขาก่อนส่งตรวจสอบ' : undefined}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                กำลังประมวลผล...
              </>
            ) : (
              "ตรวจสอบ"
            )}
          </Button>
        </div>
        </>)}
      </DialogContent>
    </Dialog>
  )
}
